/**
 * Instruction Node Component
 *
 * Represents a script execution step in the story flow.
 * Executes one or more statements that modify variables during play mode.
 * Displays as a code-chip styled node with validation.
 *
 * The instruction script is executed in sequence:
 * - Can contain multiple statements separated by semicolons
 * - Modifies game state (variables) during play
 * - Examples: `quest.stage = 2`, `gold += 100; health -= 20`
 *
 * Invalid scripts are marked with an error badge and prevent export.
 *
 * @module model/nodeTypes/instruction
 */

import React, { useCallback, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { parseScript } from '../../scripting/parser';
import type { Node as FlowNode } from '@xyflow/react';

/**
 * Instruction Node Data Type
 *
 * Defines the structure of data stored in an Instruction node.
 * The `script` contains one or more statements that will execute in sequence.
 */
export type InstructionNodeData = {
  script: string;
  name?: string;
  description?: string;
};

/**
 * Instruction Node Metadata
 *
 * Registry entry for the Instruction node type.
 * Includes display information, schema, and default values.
 */
export const instructionNodeMeta = {
  type: 'instruction' as const,
  label: 'Instruction',
  icon: '⚙️',
  description: 'Execute a script to modify variables',
  category: 'logic',
  defaultData: {
    script: '',
    name: 'Instruction',
    description: '',
  } as InstructionNodeData,
  handles: {
    inputs: [{ id: 'in', label: 'In', position: Position.Left }],
    outputs: [{ id: 'out', label: 'Out', position: Position.Right }],
  },
};

/**
 * Parse and validate the instruction script
 *
 * Returns one of three states (mirrors condition.tsx's validateCondition):
 * - `valid: true` — script parses successfully
 * - `valid: false, isEmpty: true` — nothing typed yet (a brand new node's
 *   default state; not an error, just unconfigured)
 * - `valid: false, isEmpty: false` — non-empty but fails to parse, a real error
 *
 * Distinguishing "empty" from "broken" matters for the UI: a freshly
 * dropped node has an empty script by default, and showing a red error
 * badge on every single node the moment it's created reads as "this is
 * broken" rather than "please configure me".
 *
 * @param script The instruction script to validate
 * @returns Validation result with error and suggestions
 */
function validateInstruction(script: string): {
  valid: boolean;
  isEmpty: boolean;
  error?: string;
  suggestions?: string[];
} {
  if (!script || !script.trim()) {
    return {
      valid: false,
      isEmpty: true,
    };
  }

  const parseResult = parseScript(script);
  if (!parseResult.success) {
    return {
      valid: false,
      isEmpty: false,
      error: parseResult.error || 'Parse error',
    };
  }

  const suggestions: string[] = [];

  // Check for common style issues
  if (script.trim().endsWith(';')) {
    suggestions.push('Consider removing trailing semicolon');
  }

  // Warn if script doesn't contain any assignments (likely a condition)
  if (!script.includes('=') && !script.includes('+=') && !script.includes('-=')) {
    suggestions.push(
      'This script does not contain any assignments. ' +
      'Did you mean to create a Condition node instead?'
    );
  }

  return { valid: true, isEmpty: false, suggestions };
}

/**
 * Instruction Node Component
 *
 * Renders a rectangular node (like a code snippet) with:
 * - Script input field (mono-space font for code)
 * - Validation badge (checkmark or error icon)
 * - Input handle (left) and output handle (right)
 * - Double-click to edit in expanded mode
 * - Suggestions displayed on hover
 *
 * The node's visual appearance adjusts based on:
 * - Validation state (border color changes on error)
 * - Selection state (React Flow's default selection styling)
 * - Whether it contains suggestions (warning indicator)
 */
export const InstructionNode = React.memo((props: any) => {
  const { data, selected, isConnectable } = props;
  const { setNodes } = useReactFlow();
  const [isEditing, setIsEditing] = React.useState(false);
  const [tempScript, setTempScript] = React.useState(data.script);

  const validation = useMemo(() => validateInstruction(data.script), [data.script]);

  /**
   * Handle script changes
   *
   * Updates the node data in the store. The parent component handles
   * propagating changes to the server.
   */
  const handleScriptChange = useCallback(
    (script: string) => {
      setNodes((nodes: FlowNode[]) =>
        nodes.map((n) =>
          n.id === props.id
            ? { ...n, data: { ...n.data, script } }
            : n
        )
      );
    },
    [props.id, setNodes]
  );

  const handleSaveEdit = () => {
    handleScriptChange(tempScript);
    setIsEditing(false);
  };

  if (isEditing) {
    // Expanded editing mode
    return (
      <div
        className="nopan nodrag border border-blue-500 rounded-lg bg-white dark:bg-slate-800 p-4 w-96 shadow-lg z-50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <div className="mb-3">
          <label className="block text-sm font-semibold mb-2">Instruction Script</label>
          <textarea
            value={tempScript}
            onChange={(e) => setTempScript(e.target.value)}
            className="w-full p-2 border rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 dark:bg-slate-900"
            rows={6}
            placeholder={`e.g., quest.stage = 2\ngold += 50\nhealth -= 10`}
            autoFocus
          />
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={handleSaveEdit}
            className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded font-medium"
          >
            Save
          </button>
          <button
            onClick={() => {
              setTempScript(data.script);
              setIsEditing(false);
            }}
            className="flex-1 px-3 py-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700 text-sm rounded font-medium"
          >
            Cancel
          </button>
        </div>

        {/* Validation message */}
        {!validation.valid && !validation.isEmpty && (
          <div className="mb-3 p-2 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-200">
            <strong>Error:</strong> {validation.error}
          </div>
        )}

        {/* Suggestions */}
        {validation.valid && validation.suggestions && validation.suggestions.length > 0 && (
          <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded text-sm text-yellow-700 dark:text-yellow-200">
            <strong>Suggestions:</strong>
            <ul className="mt-1 ml-4 list-disc">
              {validation.suggestions.map((suggestion, idx) => (
                <li key={idx}>{suggestion}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Compact display mode
  return (
    <div
      className={`
        nopan border-2 rounded p-3 bg-white dark:bg-slate-800 cursor-pointer
        transition-colors duration-200 min-w-[180px]
        ${
          validation.valid
            ? 'border-green-400 dark:border-green-600'
            : validation.isEmpty
            ? 'border-gray-300 dark:border-gray-600'
            : 'border-red-400 dark:border-red-600'
        }
        ${selected ? 'ring-2 ring-blue-400' : ''}
      `}
      onDoubleClick={() => setIsEditing(true)}
    >
      {/* Left input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="in"
        isConnectable={isConnectable}
        className="w-3 h-3"
      />

      {/* Main content area */}
      <div className="mb-2">
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
          {data.name || 'Instruction'}
        </div>

        {/* Script preview (truncated) */}
        {data.script && (
          <div title={data.script} className="text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap overflow-hidden text-ellipsis bg-slate-100 dark:bg-slate-900 p-1 rounded border border-slate-200 dark:border-slate-700">
            {data.script}
          </div>
        )}
      </div>

      {/* Validation and warning badges */}
      <div className="flex gap-2 items-center justify-between flex-wrap">
        <div
          className={`text-xs font-medium px-2 py-1 rounded ${
            validation.valid
              ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200'
              : validation.isEmpty
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300'
              : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200'
          }`}
          title={validation.isEmpty ? 'Double-click to set a script' : validation.error}
        >
          {validation.valid ? '✓ Valid' : validation.isEmpty ? 'Not set' : '✗ Error'}
        </div>

        {/* Warning badge if there are suggestions */}
        {validation.valid && validation.suggestions && validation.suggestions.length > 0 && (
          <div className="text-xs font-medium px-2 py-1 rounded bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-200">
            {validation.suggestions.length} tip{validation.suggestions.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Right output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="out"
        isConnectable={isConnectable}
        className="w-3 h-3"
      />
    </div>
  );
});

InstructionNode.displayName = 'InstructionNode';
